/**
 * Browser-side OpenTelemetry tracing.
 *
 * Initializes a WebTracerProvider that exports spans to the API's
 * /telemetry/traces proxy endpoint (which forwards to the OTel collector
 * sidecar, then on to Honeycomb).
 *
 * All client-side instrumentation — Zero query metrics, page loads, long
 * tasks, errors — creates real OTel spans through this provider.
 *
 * Trace design:
 *  - A real ui.page root span anchors each page load trace. The SDK
 *    generates the trace ID and span ID, so Honeycomb sees a proper root.
 *  - Browser spans (ui.query, ui.mutation, ui.page-load, etc.) are
 *    children of ui.page, sharing its trace ID.
 *  - getTraceparent() generates a fresh trace ID per WebSocket message
 *    so server-side traces stay small and focused (one per Zero message).
 *  - Both sides carry session.id for cross-trace correlation.
 *
 * Exports:
 *  - `getTraceparent()` for Zero 1.3's native trace propagation
 *  - `emitSpan()` to create spans within the current page trace
 *  - `resetPageTrace()` to start a new trace on navigation
 *  - `flushTraces()` for visibility-change flushing
 */

import {
  context,
  DiagLogLevel,
  diag,
  type Span,
  TraceFlags,
  trace,
} from "@opentelemetry/api";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";
import { config } from "../environment";
import { telemetryTracesPath } from "./apiPaths";

let provider: WebTracerProvider | undefined;

// Per-page trace context for browser-side spans only.
// pageRootSpan is a real exported span that anchors the trace.
let currentTraceId: string | undefined;
let pageRootSpanId: string | undefined;
let pageRootSpan: Span | undefined;

export function initBrowserTracing(
  token: string,
  externalApiUrl: string,
  tenantId: string
): void {
  if (provider) return;

  // Suppress OTel diagnostic logging so export failures (e.g. from ad
  // blockers blocking /telemetry/traces) don't pollute the console.
  diag.setLogger(
    { debug() {}, info() {}, warn() {}, error() {}, verbose() {} },
    DiagLogLevel.NONE
  );

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: "ui",
    [ATTR_SERVICE_VERSION]: config.gitSha,
    "tenant.id": tenantId,
    ...(config.stackName ? { "stack.name": config.stackName } : {}),
  });

  const exporter = new OTLPTraceExporter({
    url: `${externalApiUrl}${telemetryTracesPath()}`,
    headers: { "x-tracing-token": token },
  });

  provider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(exporter)],
  });

  provider.register();
  // Caller is expected to call resetPageTrace() with session.id after init;
  // starting a span here would leave the first page's ui.page without it.
}

/** End the page root span and flush pending spans (call on visibilitychange → hidden). */
export function flushTraces(): void {
  pageRootSpan?.end();
  pageRootSpan = undefined;
  provider?.forceFlush().catch(() => {});
}

/**
 * Start a new page trace. Ends the previous root span and starts a new
 * one. The SDK generates the trace ID and span ID, so Honeycomb sees a
 * proper root with no "missing span" warnings.
 */
export function resetPageTrace(attrs?: Record<string, string | number>): void {
  pageRootSpan?.end();
  pageRootSpan = undefined;
  currentTraceId = undefined;
  pageRootSpanId = undefined;

  if (!provider) return;

  const tracer = trace.getTracer("3b-ui");
  pageRootSpan = tracer.startSpan("ui.page", {
    attributes: { "page.path": location.pathname, ...attrs },
  });

  const ctx = pageRootSpan.spanContext();
  currentTraceId = ctx.traceId;
  pageRootSpanId = ctx.spanId;
}

/**
 * Emit a span within the current page trace. All browser spans are
 * children of the ui.page root span, sharing its trace ID.
 */
export function emitSpan(
  name: string,
  attrs: Record<string, string | number>,
  durationMs?: number
): void {
  if (!provider || !currentTraceId || !pageRootSpanId) return;

  const tracer = trace.getTracer("3b-ui");
  const now = Date.now();
  const startTime = durationMs != null ? now - durationMs : now;

  const parentCtx = trace.setSpanContext(context.active(), {
    traceId: currentTraceId,
    spanId: pageRootSpanId,
    traceFlags: TraceFlags.SAMPLED,
    isRemote: true,
  });

  const span = tracer.startSpan(
    name,
    { startTime, attributes: attrs },
    parentCtx
  );
  span.end(now);
}

/**
 * Returns a fresh W3C traceparent for each Zero WebSocket message.
 *
 * Each call gets its own trace ID so server-side traces (view syncer,
 * API, pg.query) stay small and focused — one trace per message rather
 * than one giant trace per page session. Correlate browser and server
 * traces via session.id, which is on both sides.
 */
export function getTraceparent(): string | undefined {
  if (!provider) return undefined;
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  const traceId = hexString(bytes, 0, 16);
  const spanId = hexString(bytes, 16, 8);
  return `00-${traceId}-${spanId}-01`;
}

function hexString(bytes: Uint8Array, offset: number, length: number): string {
  let hex = "";
  for (let i = offset; i < offset + length; i++) {
    hex += bytes[i]!.toString(16).padStart(2, "0");
  }
  return hex;
}
